// Auth
import type { Vertical, ClinicInfo } from "@/lib/types";

export type { Vertical, ClinicInfo };

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  user: {
    id: string;
    email: string;
    role: "ADMIN" | "DOCTOR" | "RECEPTIONIST";
    name?: string;
    clinic?: ClinicInfo | null;
  };
}

export interface User {
  id: string;
  email: string;
  role: "ADMIN" | "DOCTOR" | "RECEPTIONIST";
  name?: string;
  avatarUrl?: string;
  clinic?: ClinicInfo | null;
}

// Pagination
export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Patient
export interface Patient {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  cpf?: string;
  birthDate?: string;
  gender?: "MALE" | "FEMALE" | "OTHER" | "PREFER_NOT_TO_SAY";
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  notes?: string;
  allergies?: string;
  medications?: string;
  status: "ACTIVE" | "INACTIVE" | "BLOCKED";
  createdAt: string;
  updatedAt: string;
  _count?: {
    appointments: number;
  };
}

// Professional
export interface Professional {
  id: string;
  userId: string;
  clinicId: string;
  name: string;
  crm?: string;
  specialty?: string;
  phone?: string;
  bio?: string;
  avatarUrl?: string;
  workingHours?: Record<string, { start: string; end: string } | null>;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  user?: {
    email: string;
    role: string;
  };
  _count?: {
    appointments: number;
  };
}

// Procedure
export interface Procedure {
  id: string;
  clinicId: string;
  name: string;
  description?: string;
  category?: string;
  duration: number;
  price: string | number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Appointment
export interface Appointment {
  id: string;
  patientId: string;
  professionalId: string;
  procedureId: string;
  date: string;
  startTime: string;
  endTime: string;
  status: "SCHEDULED" | "CONFIRMED" | "IN_PROGRESS" | "COMPLETED" | "CANCELLED" | "NO_SHOW";
  notes?: string;
  cancelReason?: string;
  confirmedAt?: string;
  completedAt?: string;
  cancelledAt?: string;
  createdAt: string;
  updatedAt: string;
  patient?: Pick<Patient, "id" | "name" | "phone">;
  professional?: Pick<Professional, "id" | "name">;
  procedure?: Pick<Procedure, "id" | "name" | "duration" | "price">;
}

// Transaction
export interface Transaction {
  id: string;
  patientId: string;
  appointmentId?: string;
  procedureId: string;
  amount: string | number;
  discount: string | number;
  finalAmount: string | number;
  paymentMethod: "CASH" | "CREDIT_CARD" | "DEBIT_CARD" | "PIX" | "BANK_TRANSFER" | "HEALTH_INSURANCE";
  status: "PENDING" | "COMPLETED" | "REFUNDED" | "CANCELLED";
  paidAt?: string;
  refundedAt?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  patient?: Pick<Patient, "id" | "name">;
  procedure?: Pick<Procedure, "id" | "name">;
}

// Financial Summary
export interface FinancialSummary {
  totalRevenue: number;
  transactionCount: number;
  averageTicket: number;
  byPaymentMethod: Array<{
    method: string;
    total: number;
    count: number;
  }>;
  byProcedure: Array<{
    procedureId: string;
    procedureName: string;
    total: number;
    count: number;
  }>;
}
